/*!
 * StudiFi calc engine — Pension mechanics
 *
 * MIT when lifted. Pure functions.
 *
 * Two contribution methods matter:
 * - Relief-at-source: employee pays from NET, provider claims 20%
 *   basic-rate relief for them. Higher-rate relief is claimed via
 *   self-assessment (or coded into PAYE).
 * - Salary sacrifice: employer reduces gross salary and pays the
 *   difference directly to the pension. Escapes BOTH income tax
 *   AND NI on the sacrificed amount. Almost always better if the
 *   employer offers it.
 */

/**
 * Compute the true cost of a gross pension contribution to the employee.
 *
 * @param {number} grossContribution   Amount going INTO the pension (£)
 * @param {number} marginalTaxRate     0.20, 0.40, or 0.45
 * @param {number} marginalNIRate      0.08 or 0.02 (only relevant for salary sacrifice)
 * @param {"reliefAtSource"|"salarySacrifice"} method
 * @returns {number}                   Net cost from take-home (£)
 */
export function netCostOfContribution(
  grossContribution,
  marginalTaxRate,
  marginalNIRate,
  method,
) {
  if (method === "salarySacrifice") {
    return grossContribution * (1 - marginalTaxRate - marginalNIRate);
  }
  if (method === "reliefAtSource") {
    return grossContribution * (1 - marginalTaxRate);
  }
  throw new Error(`Unknown pension method: ${method}`);
}

/**
 * Compute the effective return on £1 of the employee's own money
 * contributed to a pension, at the point it enters the pension.
 *
 * £1 contributed → £X in the pension (before growth). This captures
 * tax relief + employer match. It's the number to compare with any
 * other use of £1 (loan overpayment, ISA, LISA).
 *
 * @param {object} params
 * @param {number} params.marginalTaxRate
 * @param {number} params.marginalNIRate
 * @param {"reliefAtSource"|"salarySacrifice"} params.method
 * @param {number} params.employerMatchRatio  e.g. 1.0 = £1 employer for £1 employee, 0.5 = 50p per £1
 * @param {number} params.employeeContribution  Employee gross contribution (£/yr)
 * @param {number} params.employerMatchCap  Max employee £/yr the employer will match
 * @returns {{
 *   employeeGross: number,
 *   employeeNetCost: number,
 *   employerMatch: number,
 *   totalIntoPension: number,
 *   returnMultiple: number
 * }}
 */
export function contributionReturn(params) {
  const {
    marginalTaxRate,
    marginalNIRate,
    method,
    employerMatchRatio,
    employeeContribution,
    employerMatchCap,
  } = params;

  const employeeNetCost = netCostOfContribution(
    employeeContribution,
    marginalTaxRate,
    marginalNIRate,
    method,
  );

  const matchedEmployeeAmount = Math.min(employeeContribution, employerMatchCap);
  const employerMatch = matchedEmployeeAmount * employerMatchRatio;
  const totalIntoPension = employeeContribution + employerMatch;

  const returnMultiple = employeeNetCost > 0 ? totalIntoPension / employeeNetCost : Infinity;

  return {
    employeeGross: employeeContribution,
    employeeNetCost,
    employerMatch,
    totalIntoPension,
    returnMultiple,
  };
}

/**
 * Compute a single-year "scenario": what goes into the pension, at what
 * net cost to the employee, and what the resulting salary is for
 * student-loan purposes.
 *
 * Salary sacrifice reduces the reported salary — so income tax, NI AND
 * student-loan repayments are all calculated on the lower figure.
 * Relief-at-source does NOT reduce reported salary — the contribution
 * comes out of net pay and the provider claims basic-rate tax relief.
 *
 * @param {object} p
 * @param {number} p.salary                  Gross annual salary before pension (£)
 * @param {"salarySacrifice"|"reliefAtSource"} p.method
 * @param {number} p.employeePct             Employee contribution % of salary (e.g. 5 for 5%)
 * @param {number} p.employerBasePct         Unconditional employer % (e.g. 3 for auto-enrolment min)
 * @param {number} p.employerMatchRatio      £ employer per £ employee (e.g. 1.0 for 1:1)
 * @param {number} p.employerMatchCapPct     Max employee % the employer will match
 * @param {number} p.marginalTaxRate         Decimal (0.20, 0.40, 0.60, 0.45)
 * @param {number} p.marginalNIRate          Decimal (0.08 or 0.02)
 * @returns {{
 *   employeeGross: number,
 *   employeeNetCost: number,
 *   employerBase: number,
 *   employerMatch: number,
 *   totalIntoPension: number,
 *   slcRelevantSalary: number,
 *   returnMultiple: number
 * }}
 */
export function pensionScenario(p) {
  const {
    salary,
    method,
    employeePct,
    employerBasePct,
    employerMatchRatio,
    employerMatchCapPct,
    marginalTaxRate,
    marginalNIRate,
  } = p;

  const employeeGross = salary * (employeePct / 100);
  const employerBase = salary * (employerBasePct / 100);

  const matchableEmployeePct = Math.min(employeePct, employerMatchCapPct);
  const matchableEmployee = salary * (matchableEmployeePct / 100);
  const employerMatch = matchableEmployee * employerMatchRatio;

  const totalIntoPension = employeeGross + employerBase + employerMatch;

  const employeeNetCost = netCostOfContribution(
    employeeGross,
    marginalTaxRate,
    marginalNIRate,
    method,
  );

  const slcRelevantSalary =
    method === "salarySacrifice" ? salary - employeeGross : salary;

  const returnMultiple =
    employeeNetCost > 0 ? totalIntoPension / employeeNetCost : Infinity;

  return {
    employeeGross,
    employeeNetCost,
    employerBase,
    employerMatch,
    totalIntoPension,
    slcRelevantSalary,
    returnMultiple,
  };
}

/**
 * Determine an employee's marginal tax + NI rates from their salary
 * for the England/Wales/NI tax bands. Returns the highest-band rates
 * that would apply to the NEXT pound of salary — i.e. the rates
 * relevant to any pension contribution or salary bump.
 *
 * The £100k–£125,140 personal-allowance taper is modelled as a 60%
 * effective marginal rate.
 *
 * @param {number} salary
 * @param {object} band     taxData.englandNIWales (or scotland)
 * @param {object} niData   nationalInsurance.class1Employee
 * @returns {{ tax: number, ni: number, combined: number }}
 */
export function marginalRates(salary, band, niData) {
  let tax;
  if (salary > 125140) tax = 0.45;
  else if (salary > 100000) tax = 0.60; // PA-taper 40% + effective 20% from lost PA
  else if (salary > 50270) tax = 0.40;
  else if (salary > band.personalAllowance) tax = 0.20;
  else tax = 0;

  const ni = salary > niData.upperEarningsLimit ? niData.upperRate
    : salary > niData.primaryThreshold ? niData.mainRate
    : 0;

  return { tax, ni, combined: tax + ni };
}

/**
 * Grow a pension pot forward n years given a real-return assumption
 * and constant annual contributions.
 *
 * All figures are in TODAY'S money because we compound at a REAL
 * return (nominal minus inflation). This keeps the projection
 * interpretable without a separate inflation adjustment.
 *
 * Contributions are added at year end (arrears convention).
 *
 * @param {object} params
 * @param {number} params.startingPot          Opening pot value (£, today's money)
 * @param {number} params.annualContribution   Total going into pot each year (employee + employer + relief)
 * @param {number} params.years                Whole years to project
 * @param {number} params.realReturn           Decimal, e.g. 0.045 = 4.5% real
 * @param {number} [params.contributionGrowth] Optional; contribution grows this fraction each year (e.g. 0.03 with salary)
 * @returns {{
 *   closingPot: number,
 *   contributionsTotal: number,
 *   growthTotal: number,
 *   byYear: Array<{ year: number, openingPot: number, growth: number, contribution: number, closingPot: number }>
 * }}
 */
export function projectPot(params) {
  const {
    startingPot,
    annualContribution,
    years,
    realReturn,
    contributionGrowth = 0,
  } = params;

  const byYear = [];
  let pot = startingPot;
  let contribution = annualContribution;
  let contributionsTotal = 0;

  for (let y = 1; y <= years; y++) {
    const openingPot = pot;
    const growth = pot * realReturn;
    pot += growth + contribution;
    contributionsTotal += contribution;

    byYear.push({
      year: y,
      openingPot,
      growth,
      contribution,
      closingPot: pot,
    });

    contribution *= 1 + contributionGrowth;
  }

  return {
    closingPot: pot,
    contributionsTotal,
    growthTotal: pot - startingPot - contributionsTotal,
    byYear,
  };
}
